import React from 'react';
import PropTypes from 'prop-types';
import MenuItemComponent from './MenuItemComponent';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import List from '@material-ui/core/List';
import { useDispatch, useSelector } from 'react-redux';
import { DetectIsMobile } from 'utils/mobileDetect';
import {
  selectAuthenticatedUser,
  selectIsExpandedSideBar,
  selectSiteMapProvider,
  selectSiteMapSettings,
} from 'app/slice/selectors';
import { HoverSideMenu } from './HoverMenu';
import {
  AssetsSubTabUnionArray,
  MainTabsUnion,
  SiteMapProvider,
} from 'types/SiteMapSettings';
import { isNullOrUndefined } from 'utils/typeUtils';
import { useLocation } from 'react-router-dom';
import useExpandedSideBarState from 'app/hooks/useExpandedSideBarState';
import { selectHasNotSavedChanges } from 'app/Layout/FrontendLayout/slice/selectors';
import { useLayoutSlice } from 'app/Layout/FrontendLayout/slice';
import {
  RowActionsMenu,
  RowActionsProps,
} from 'app/components/BasicTable/RowActions';
import clsx from 'clsx';
import BasicTypography from 'app/components/Typography/BasicTypography';
import { Icon as AwesomeIcon } from 'app/components/BasicIcons/FontAwesome';
import { Roles } from 'app/slice/types';
import { isInRole } from 'types/AuthenticatedUser';

export const MenuItemPropTypes = {
  name: PropTypes.string.isRequired,
  link: PropTypes.string,
  external: PropTypes.bool.isRequired,
  Icon: PropTypes.element,
  items: PropTypes.array,
  selected: PropTypes.bool.isRequired,
  indexChanged: PropTypes.func,
  index: PropTypes.number.isRequired,
  chIndex: PropTypes.number,
  onChange: PropTypes.func,
  closeOnClick: PropTypes.bool,
};
type MenuItemPropTypesItems = PropTypes.InferProps<typeof MenuItemPropTypes>;
type MenuItemPropTypesNoItems = Omit<MenuItemPropTypesItems, 'items'>;

export type SidebarMenuItemProps = MenuItemPropTypesNoItems & {
  items?: SidebarMenuItemProps[];
  mainTab?: MainTabsUnion;
  groupTab?: number;
  hasAdminSettings?: boolean;
};
const SidbarMenuItem = React.forwardRef<any, SidebarMenuItemProps>(
  function SidbarMenuItem(props, ref) {
    const {
      name,
      link,
      external,
      Icon,
      items = [],
      selected,
      indexChanged,
      index,
      chIndex,
      mainTab,
      hasAdminSettings,
      closeOnClick,
    } = props;
    //const classes = useStyles();
    const isExpandable = items && items.length > 0;
    const isMobile = DetectIsMobile();
    const [open, setOpen] = React.useState(selected);
    const [isSelected, setSelected] = React.useState(selected);
    const [, setChildIndex] = React.useState(chIndex);
    const hasChanges = useSelector(selectHasNotSavedChanges);
    const siteMapProvider = useSelector(selectSiteMapProvider);
    const user = useSelector(selectAuthenticatedUser);
    const dispatch = useDispatch();
    const { actions } = useLayoutSlice();
    const sidebarExpanded = useSelector(state =>
      selectIsExpandedSideBar(state, isMobile),
    );
    const { expandLocal } = useExpandedSideBarState();
    const siteMapSettings = useSelector(selectSiteMapSettings);
    const location = useLocation();
    const isAdmin =
      isInRole(user, Roles.Administrators) ||
      isInRole(user, Roles.GroupAdministrators) ||
      isInRole(user, Roles.CustomRole);
    const [urlLink, setUrlLink] = React.useState<string | null | undefined>(
      link,
    );
    // const handleSettingClick = React.useCallback(
    //   (link: string) => {
    //     dispatch(
    //       actions.openAdminPanel({
    //         url: link,
    //       }),
    //     );
    //   },
    //   [actions, dispatch],
    // );
    const adminSettings = React.useMemo(() => {
      let actions: RowActionsProps[] = [];
      if (!!mainTab && isAdmin && hasAdminSettings) {
        if (mainTab === 'Assets') {
          AssetsSubTabUnionArray.forEach(ar => {
            actions = [
              ...actions,
              ...siteMapProvider.adminSettingsActions(
                mainTab,
                user,
                ar,
                undefined, // handleSettingClick,
              ),
            ];
          });
        } else {
          if (mainTab === 'Budgets') {
            return siteMapProvider.adminSettingsActions(
              mainTab,
              user,
              'BudgetTabSetting',
              undefined, // handleSettingClick,
            );
          }
          return siteMapProvider.adminSettingsActions(
            mainTab,
            user,
            undefined,
            undefined, // handleSettingClick,
          );
        }
      }
      return actions;
    }, [hasAdminSettings, isAdmin, mainTab, siteMapProvider, user]);
    React.useEffect(() => {
      let active =
        !isNullOrUndefined(siteMapSettings) && link === location?.pathname;
      if (active) {
        let siteMapProvider = new SiteMapProvider(siteMapSettings);
        let node = siteMapProvider.findNode(
          location?.pathname || window.location.pathname,
        );

        setUrlLink(() => {
          if (node !== undefined) {
            const reactUrl = node.reactUrl;
            if (reactUrl !== undefined && reactUrl !== null) {
              const elementPath = reactUrl.split('?')[0] ?? reactUrl;
              if (elementPath === location.pathname) {
                return reactUrl;
              } else {
                return link;
              }
            } else {
              return link;
            }
          } else {
            return link;
          }
        });
      }
      return () => {
        active = false;
      };
    }, [link, location.pathname, siteMapSettings]);
    function handleClick(event, index) {
      if (
        !!event.target &&
        !!event.target.parentNode &&
        'className' in event.target.parentNode &&
        event.target.parentNode.className !== undefined &&
        event.target.parentNode.className !== null &&
        event.target.parentNode.className !== '' &&
        typeof event.target.parentNode.className === 'string' &&
        event.target.parentNode.className.indexOf('MuiPopover') > -1
      ) {
        event.preventDefault();
        event.stopPropagation();
      } else {
        if (
          hasChanges &&
          (props.items === undefined || props.items.length < 1)
        ) {
          dispatch(
            actions.setContinueToLink(
              props.link === null || props.link === undefined
                ? location.pathname
                : props.link,
            ),
          );
          dispatch(actions.setConfirmOpen(true));
          event.preventDefault();
        } else {
          setOpen(!open);
          if (props.index !== index) {
            setSelected(!isSelected);
          } else {
            if (props.items !== undefined && props.items?.length > 0) {
              setSelected(!selected);
            }
          }
          if (indexChanged !== undefined && indexChanged !== null) {
            indexChanged(event, index);
          }
        }
        if (isMobile && closeOnClick) {
          expandLocal(false);
        }
      }
    }
    const handleChildClick = (event, index) => {
      if (hasChanges) {
        dispatch(
          actions.setContinueToLink(
            props.link === null || props.link === undefined
              ? location.pathname
              : props.link,
          ),
        );
        dispatch(actions.setConfirmOpen(true));
        event.preventDefault();
      } else {
        if (isMobile) {
          expandLocal(false);
        }
        setChildIndex(prev => {
          if (props.chIndex !== index) {
            return index;
          }
        });
        setChildIndex(index);
      }
    };

    const parentId = `menuitem-${index}`;
    return !sidebarExpanded ? ( //isExpandable &&
      <HoverSideMenu
        ref={ref}
        items={items}
        selected={selected}
        handleClick={handleClick}
        handleChildClick={handleChildClick}
        //onExternalClick={handleSettingClick}
        external={external}
        id={`index-${index}`}
        index={index}
        link={urlLink || link}
        Icon={Icon}
        name={name}
      />
    ) : (
      <>
        <MenuItemComponent
          id={`${parentId}${chIndex === undefined ? '' : '-' + chIndex}`}
          className={clsx('menuItem', {
            mainTabMenu: mainTab !== undefined,
            mainTabExpanded: sidebarExpanded,
            mainTabShort: !sidebarExpanded,
            mainTabOpen: open,
          })}
          role={isExpandable ? 'menu' : 'menuitem'}
          link={urlLink || link}
          onClick={handleClick}
          external={external}
          selected={selected}
          //onExternalClick={handleSettingClick}
          currentIndex={index}
        >
          {/* Display an icon if any */}
          <div className="menuItemContent">
            {!!Icon && (
              <ListItemIcon className="menuItemIcon">{Icon}</ListItemIcon>
            )}
            {/* <ListItemText
              className={!Icon ? 'menuItemTextInset' : 'menuItemText'}
              primary={name}
              inset={!Icon}
            /> */}
            <BasicTypography
              className={'menuItemText'}
              display="block"
              variant="buttonS"
            >
              {name}
            </BasicTypography>
          </div>
          {!!mainTab &&
            hasAdminSettings &&
            !isMobile &&
            adminSettings.length > 0 && (
              // <ListItemSecondaryAction className="secondary-icon">

              // </ListItemSecondaryAction>
              <RowActionsMenu
                id={`${mainTab}_settings`}
                variant="textGray"
                onClick={event => {
                  event.stopPropagation();
                  event.preventDefault();
                }}
                items={adminSettings}
                // items={settingItems.map(row => {
                //   return {
                //     //href: row.link,
                //     text: row.name,
                //     icon: 'circle-small',
                //     onClick: () => handleSettingClick(row.link ?? ''),
                //   } as RowActionsProps;
                // })}
              />
            )}
          {isExpandable && (
            <AwesomeIcon
              icon={
                (open || isSelected) && selected
                  ? 'chevron-up'
                  : isMobile
                  ? 'chevron-down'
                  : 'chevron-right'
              }
              size="sm"
            />
          )}
        </MenuItemComponent>

        {/* Display the expand menu if the item has children */}
        {isExpandable && (open || isSelected) && selected && (
          <List
            component="div"
            className="secondMenuItem"
            disablePadding
            role="menu"
            data-parent-id={parentId}
          >
            {items.map((item, chIndex) => (
              <SidbarMenuItem
                {...item}
                index={index}
                chIndex={chIndex}
                key={chIndex}
                indexChanged={handleChildClick}
                selected={selected && item.selected}
              />
            ))}
          </List>
        )}
      </>
    );
  },
);

export default SidbarMenuItem;
